import CopyIcon from "@/components/icons/CopyIcon";
import { useNotify } from "@/hooks/useNotify";
import React, { FC, useState, useCallback } from "react";
import { createPortal } from
"react-dom";


const UPPERCASE = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LOWERCASE = "abcdefghijklmnopqrstuvwxyz";
const DIGITS = "0123456789";
const SPECIAL = "@$!%*?&";
const ALL_CHARS = UPPERCASE + LOWERCASE + DIGITS + SPECIAL;

function cryptoRandom(max: number): number {
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  return arr[0] % max;
}

function generateStrongPassword(length = 16): string {
  const required = [UPPERCASE, LOWERCASE, DIGITS, SPECIAL].map(
    (s) => s[cryptoRandom(s.length)]
  );
  const rest = Array.from(
    { length: length - required.length },
    () => ALL_CHARS[cryptoRandom(ALL_CHARS.length)]
  );
  const all = [...required, ...rest];
  for (let i = all.length - 1; i > 0; i--) {
    const j = cryptoRandom(i + 1);
    [all[i], all[j]] = [all[j], all[i]];
  }
  return all.join("");
}

interface PasswordGeneratorPopupProps {
  onUse: (password: string) => void;
  onClose: () => void;
}

const PasswordGeneratorPopup: FC<PasswordGeneratorPopupProps> = ({ onUse, onClose }) => {
  const [generated, setGenerated] = useState("");
  const [length, setLength] = useState(16);
  const [copied, setCopied] = useState(false);
  const notify = useNotify();


  const handleGenerate = useCallback(() => {
    setGenerated(generateStrongPassword(length));
    setCopied(false);
  }, [length]);

  const handleCopy = useCallback(() => {
    if (!generated) return;
    navigator.clipboard.writeText(generated).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [generated]);

  const handleUse = useCallback(() => {
    onUse(generated);
    navigator.clipboard.writeText(generated);
    notify(
      "info",
      
      "Password copied successfully. Please save it securely",
      ""

    );
    onClose();
  }, [generated, onUse, onClose]);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white border border-gray-200 rounded-xl p-6 w-[420px] max-w-[95vw] shadow-xl">

        <div className="flex items-center justify-between mb-5">
          <p className="flex items-center gap-2 text-[15px] font-medium text-[#111]">
            🔒 Password generator
          </p>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-gray-100">
            ✕
          </button>
        </div>

        <span className="block text-[13px] text-gray-500 mb-1.5">Generated password</span>
        <div className="flex items-center gap-2 mb-5">
          <input
            type="text"
            readOnly
            value={generated}
            placeholder="Click generate…"
            className="flex-1 border border-[#D3D3D3] rounded-md h-[38px] px-3 font-mono text-[15px] tracking-wider bg-[#fafafa] text-[#333] outline-none focus:border-gray-400 focus:bg-white"
          />
          <button
            type="button"
            onClick={handleCopy}
            disabled={!generated}
            className="flex-shrink-0 h-[38px] px-3 border border-[#D3D3D3] rounded-md text-[13px] text-gray-500 flex items-center gap-1.5 hover:bg-gray-50 hover:text-gray-800 disabled:opacity-40 transition-colors"
          >
            <CopyIcon/> {copied ? "Copied!" : "Copy"}
          </button>
        </div>

        <hr className="border-t border-gray-100 my-5" />

        <div className="mb-5">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-[13px] text-gray-500">Length</span>
            <span className="text-[14px] font-medium text-[#111]">{length}</span>
          </div>
          <input
            type="range"
            min={8}
            max={32}
            step={1}
            value={length}
            onChange={(e) => setLength(Number(e.target.value))}
            className="w-full accent-gray-800"
          />
          <div className="flex justify-between text-[12px] text-gray-400 mt-1">
            <span>8</span><span>32</span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleGenerate}
          className="w-full h-10 border border-[#D3D3D3] rounded-md text-[14px] font-medium text-[#111] flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors mb-2"
        >
          ↻ Generate password
        </button>

        <button
          type="button"
          onClick={handleUse}
          disabled={!generated}
          className="w-full h-10 bg-[#222] text-white rounded-md text-[14px] font-medium flex items-center justify-center hover:bg-[#444] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          Use this password
        </button>

      </div>
    </div>,
    document.body
  );
};

export default PasswordGeneratorPopup;